// Order is important with commands!
import Command, { Argument } from "./Command";
import { escapeRegExp } from "./MiscUtils";

const createMathSymbolRegexes = (symbol: string) => {
  return [
    new RegExp(`^(.*?) ${escapeRegExp(symbol)} (.+)$`),
    new RegExp(`^(-?[\\d.]*?)\\s*${escapeRegExp(symbol)}\\s*(.+)$`),
    new RegExp(`^(.*?)\\s*${escapeRegExp(symbol)}\\s*(-?[\\d.]+)$`),
    new RegExp(`^(-?[\\d.]*?)\\s*${escapeRegExp(symbol)}\\s*(-?[\\d.]+)$`),
  ];
};

const createSymbolRegexes = (symbol: string) => {
  return [ new RegExp(`^(.+)\s*${escapeRegExp(symbol)}\s*(.+)$`) ];
};

const CONDITION_PRIORITY = 10;

export const INFER_INSTANCES_COMMAND = new Command({
  id: "infer-instances",
  arguments: [ Argument.required("relationship", "relationship"), Argument.required("id", "auto") ],
  definition: [],
  displayName: "Infer Instances",
});

export const INFER_INSTANCE_COMMAND = new Command({
  id: "infer-instance",
  arguments: [ Argument.required("relationship", "relationship"), Argument.required("id", "auto") ],
  definition: [],
  displayName: "Infer Instance",
  description: `<div>
          <div>
            Infer the existence of an entity instance to satisfy the provided relationship.
          </div>
          <br />
          <div>
            Eg: infer-instance(the first item, 99) creates an instance of 'the item', where 'the first item' specifies a one-to-one relationship from the global context to the entity 'the item'. The value of ‘the item’ for the instance that is created is set to 99.
          </div>
          <br />
        </div>`,
});

export const ORDINAL_COMMAND = new Command({
  id: "ordinal",
  arguments: [
    Argument.required("entity", "auto"),
    Argument.optional("relationship", "relationship"),
  ],
  displayName: "Ordinal",
});

export const CONTACT_START_COMMAND = new Command({
  id: "contact-start",
  arguments: [ Argument.required("attribute", "auto") ],
  displayName: "Contact Start",
});

export const CONTACT_END_COMMAND = new Command({
  id: "contact-end",
  arguments: [ Argument.required("attribute", "auto") ],
  displayName: "Contact End",
});

export const VALIDATE_COMMAND = new Command({
  id: "raise-error",
  arguments: [ Argument.required("message", "text"), Argument.optional("entity", "entity") ],
  displayName: "Raise Error",
});

export const WARNING_COMMAND = new Command({
  id: "raise-warning",
  arguments: [ Argument.required("message", "text"), Argument.optional("entity", "entity") ],
  displayName: "Raise Warning",
});

export const AND_COMMAND = new Command({
  id: "and",
  arguments: [
    new Argument({
      name: "values",
      types: "boolean",
      required: false,
      variadic: true,
    }),
  ],
  definition: [ /^all$/i ],
  description: `<div>
          <div>
            All sub conditions must be true for this condition to pass. This is generally used on a single line, with
            sub-conditions at a greater indent
          </div>
        </div>`,
  displayName: "And",
});
export const OR_COMMAND = new Command({
  id: "or",
  arguments: [
    new Argument({
      name: "values",
      types: "boolean",
      required: false,
      variadic: true,
    }),
  ],
  definition: [ /^any/i ],
  displayName: "Or",
  description: `<div>
          <div>
            Atleast one sub condition must be true for this condition to pass. This is generally used on a single line,
            with sub-conditions at a greater indent
          </div>
        </div>`,
});

export const NOT_COMMAND = new Command({
  id: "not",
  arguments: [ Argument.required("condition", "boolean") ],
  definition: [],
  displayName: "Not",
  description: `<div>
          <div>
            Returns the opposite of the condition. Note: this only works with boolean conditions. For all other types
            (number, string etc) it will return Uncertain.
          </div>
          <br />
          <div>Eg: not(the customer is approved) would return false if the customer was approved</div>
          <br />
        </div>`,
});

export const FOR_COMMAND = new Command({
  id: "for",
  arguments: [ Argument.required("relationship", "relationship"), Argument.required("attribute", "auto") ],
  definition: [],
  displayName: "For",
  description: `<div>
          <div>
            Use a relationship to access an attribute value from an instance of the target entity.
          </div>
          <br />
          <div>
            Eg: For(the previous payment, the payment date) gets the value of 'the payment date' from the instance of 'the payment' that is the target of the relationship.
          </div>
          <br />
        </div>`,
});

export const COMMANDS: Command[] = [
  new Command({
    id: "equals",
    returnType: "boolean",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [
      /^(.+)\s*==\s*(.+)$/,
      /(.+\s*[^<>=])=\s*(.*)$/,
      ///(.+) equals (.+)$/
    ],
    naturalPriority: CONDITION_PRIORITY + 5,
    displayName: "Equals",
    description: `<div>
          <div>
            Returns true if the provided values match (are the same). Note: you can also use the alternative form
            'a = b'
          </div>
          <br />
          <div>
            Eg: equals(the payment amount, the payment threshold) would return true both of these attributes were the
            same value.
          </div>
          <br />
          <div>Consider not-equals for the oppositve behaviour</div>
        </div>`,
  }),
  new Command({
    id: "equals-within-tolerance",
    returnType: "boolean",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto"), Argument.required("delta", "auto") ],
    displayName: "Equals Within Tolerance",
  }),
  new Command({
    id: "current-date",
    arguments: [],
    definition: [ /^the current date$/ ],
    displayName: "Current Date",
    description: `<div>
          <div>
            Returns the current date
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-from-start-date",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("start", "datetime"),
      Argument.required("value", "auto"),
    ],
    //definition: [/^(?<$3>.+?) of (?<$1>.+) using the start date of (?<$2>.+)$/],
    displayName: "Temporal From Start Date",
    description: `<div>
          <div>
            Create a temporal value of all instances of the provided relationship, using the start and value attributes
            to build the temporal value. This function will sort all instances by start date, and then use the next
            start date as the end date of the previous range.
          </div>
          <br />
          <div>
            Eg: temporal-from-start-date(the payments, the payment start date, the payment value) would create a single
            temporal attribute, with the payment start date as the start date for each range and the payment value as
            the value.
          </div>
          <br />
          <div>
            Given two instances of the payments with start date of 2022-01-01 and 2022-02-01 this function would produce
            a temporal value with 3 ranges:
            <ul>
              <li>From the beginning of time until 2022-01-01 with a value of unknown</li>
              <li>From 2022-01-01 until 2022-02-01 with a value of the first payment value</li>
              <li>From 2022-02-01 until the end of time with a value of second payment value</li>
            </ul>
          </div>
          <br />
          <div>Consider the temporal-from-start-date function if you need to use end dates</div>
        </div>`,
  }),
  new Command({
    id: "temporal-from-end-date",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("end", "datetime"),
      Argument.required("value", "auto"),
    ],
    //definition: [/^(?<$3>.+?) of (?<$1>.+) using the end date of (?<$2>.+)$/],
    displayName: "Temporal From End Date",
    description: `<div>
          <div>
            Create a temporal value of all instances of the provided relationship, using the end and value attributes to
            build the temporal value. This function will sort all instances by end date, and then use the previous end
            date as the start date of the next range.
          </div>
          <br />
          <div>
            Eg: temporal-from-end-date(the payments, the payment end date, the payment value) would create a single
            temporal attribute, with the payment end date as the end date for each range and the payment value as the
            value.
          </div>
          <br />
          <div>
            Given two instances of the payments with end date of 2022-01-01 and 2022-02-01 this function would produce a
            temporal value with 3 ranges:
            <ul>
              <li>From the beginning of time until 2022-01-01 with a value of the first payment value</li>
              <li>From 2022-01-01 until 2022-02-01 with a value of the second payment value</li>
              <li>From 2022-02-01 until the end of time with a value of unknown</li>
            </ul>
          </div>
          <br />
          <div>Consider the temporal-from-start-date function if you need to use start dates</div>
        </div>`,
  }),
  new Command({
    id: "temporal-from-range",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("start", "datetime"),
      Argument.required("end", "datetime"),
      Argument.required("value", "auto"),
    ],
    displayName: "Temporal From Range",
    //definition: [/^(?<$4>.+?) of (?<$1>.+) between (?<$2>.+) and (?<$3>.+)$/],
    description: `<div>
          <div>
            Create a temporal value of all instances of the provided relationship, using the start, end and value
            attributes to build the temporal value.
          </div>
          <br />
          <div>
            Eg: temporal-from-range(the payments, the payment start date, the payment end date, the payment value) would
            create a single temporal attribute, with the payment start date as the start date for each range, the
            payment end date as the end date, and the payment value as the value.
          </div>
          <br />
          <div>
            Consider temporal-from-start if you don't have or don't need the end date attribute. This function sorts the
            instances, and uses the start date of the next range as the end date of the previous.
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-always-days",
    arguments: [ Argument.required("number", "number"), Argument.required("condition", "boolean") ],
    //definition: [/^(.+) has been true for the previous ([0-9]+) days$/],
    displayName: "Temporal Always Days",
    description: `<div>
          <div>
            Temporal function that takes as input an attribute 'number' specifying a number of days, and a boolean
            attribute specifying a temporal condition. Produces a temporal attribute that indicates whether the
            preceding n days (not including the current day) all satisfy the condition.
          </div>
          <br />
          <div>
            Eg: temporal-always-day(7, the customer was approved) would check every day within the temporal range 'the
            customer was approved' was true for the preceeding 7 days. So if 'the customer was approved' was true from
            2022-01-01 until 2022-02-01, and false otherwise then the result of this function would be:
            <ul>
              <li>From the beginning of time until 2022-12-24 with a value of false</li>
              <li>From 2022-12-24 until 2022-01-01 with a value of true</li>
              <li>From 2022-01-01 until the end of tiem with a value of false</li>
            </ul>
          </div>
          <br />
        </div>`,
  }),
  new Command({
    id: "temporal-always",
    arguments: [
      Argument.required("number", "number"),
      Argument.required("unit", "string"),
      Argument.required("condition", "boolean"),
    ],
    displayName: "Temporal Always",
  }),
  new Command({
    id: "temporal-sometimes-days",
    arguments: [ Argument.required("number", "number"), Argument.required("condition", "boolean") ],
    displayName: "Temporal Sometimes Days",
  }),
  new Command({
    id: "temporal-sometimes",
    arguments: [
      Argument.required("number", "number"),
      Argument.required("unit", "string"),
      Argument.required("condition", "boolean"),
    ],
    displayName: "Temporal Sometimes",
  }),
  new Command({
    id: "temporal-on-or-after",
    arguments: [ Argument.required("date", "datetime") ],
    displayName: "Temporal On or After",
    // definition: [/^a value that is true on or after (.+)/],
    description: `<div>
          <div>Create a temporal range that is true after the provided date (inclusive).</div>
          <br />
          <div>
            Eg: temporal-on-or-after(the payment date) would create a temporal value that is true after and including
            the payment date. It would be false otherwise.
          </div>
          <br />
          <div>
            Consider temporal-after if you need to don't want to include the date provided. The temporal-before
            functions provide similar functionality, but in reverse.
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-on-or-before",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^a value that is true before (.+)/],
    displayName: "Temporal On or Before",
    description: `<div>
          <div>Create a temporal range that is true before the provided date (inclusive).</div>
          <br />
          <div>
            Eg: temporal-on-or-before(the payment date) would create a temporal value that is true before and including
            the payment date. It would be false otherwise.
          </div>
          <br />
          <div>
            Consider temporal-before if you need to don't want to include the date provided. The temporal-before
            functions provide similar functionality, but in reverse.
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-after",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^a value that is true after (.+)/],
    displayName: "Temporal After",
    description: `<div>
          <div>Create a temporal range that is true after the provided date.</div>
          <br />
          <div>
            Eg: temporal-after(the payment date) would create a temporal value that is true after (but not including)
            the payment date. It would be false otherwise.
          </div>
          <br />
          <div>
            Consider temporal-on-or-after if you need to include the date provided. The temporal-before functions
            provide similar functionality, but in reverse.
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-before",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^a value that is true before (.+)/],
    displayName: "Temporal Before",
    description: `<div>
          <div>Create a temporal range that is true before the provided date.</div>
          <br />
          <div>
            Eg: temporal-before(the payment date) would create a temporal value that is true before (but not including)
            the payment date. It would be false otherwise.
          </div>
          <br />
          <div>
            Consider temporal-on-or-before if you need to include the date provided. The temporal-after functions
            provide similar functionality, but in reverse.
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-years-since",
    arguments: [ Argument.required("from", "datetime"), Argument.required("to", "datetime") ],
    //definition: [/^every year between (.+) and (.+)/],
    displayName: "Temporal Years Since",
    description: `<div>
          <div>Create a temporal value that counts the number of years between two points (from and to)</div>
          <br />
          <div>
            Eg: temporal-years-since(1990-01-01, 2020-01-01) would produce a temporal value that counts the number of
            years from 1990 until 2020, like:
            <ul>
              <li>From the beginning of time until 1990-01-01 the value of 'Not Valid'</li>
              <li>From 1990-01-01 until 1991-01-01 the value of 0</li>
              <li>From 1991-01-01 until 1992-01-01 the value of 1</li>
              <li>From 1992-01-01 until 1993-01-01 the value of 2</li>
              <li>...</li>
              <li>From 2019-01-01 until 2020-01-01 the value of 20</li>
              <li>From 2020-01-01 until the end of time the value of 'Not Valid'</li>
            </ul>
          </div>
          <br />
          <div>
            Consider temporal-months-since and temporal-days-since for similar functionality on a different time factor
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-months-since",
    arguments: [ Argument.required("from", "datetime"), Argument.required("to", "datetime") ],
    displayName: "Temporal Months Since",
    //definition: [/^every month between (.+) and (.+)/],
    description: `<div>
          <div>Create a temporal value that counts the number of months between two points (from and to)</div>
          <br />
          <div>
            Eg: temporal-months-since(1990-01-01, 2020-01-01) would produce a temporal value that counts the number of
            months from 1990 until 2020, like:
            <ul>
              <li>From the beginning of time until 1990-01-01 the value of 'Not Valid'</li>
              <li>From 1990-01-01 until 1990-02-01 the value of 0</li>
              <li>From 1990-02-01 until 1990-03-01 the value of 1</li>
              <li>From 1990-03-01 until 1990-04-01 the value of 2</li>
              <li>...</li>
              <li>From 2019-12-01 until 2020-01-01 the value of 360</li>
              <li>From 2020-01-01 until the end of time the value of 'Not Valid'</li>
            </ul>
          </div>
          <br />
          <div>
            Consider temporal-years-since and temporal-days-since for similar functionality on a different time factor
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-weeks-since",
    arguments: [ Argument.required("from", "datetime"), Argument.required("to", "datetime") ],
    displayName: "Temporal Weeks Since",
    //definition: [/^every week between (.+) and (.+)/],
    description: `<div>
          <div>Create a temporal value that counts the number of weeks (7 day periods) between two points (from and to)</div>
          <br />
          <div>
            Eg: temporal-weeks-since(2022-01-01, 2022-02-01) would produce a temporal value that counts the number of
            weeks from 1990 until 2020, like:
            <ul>
              <li>From the beginning of time until 2022-01-01 the value of 'Not Valid'</li>
              <li>From 2022-01-01 until 2022-01-07 the value of 0</li>
              <li>From 2022-01-07 until 2022-01-14 the value of 1</li>
              <li>From 2022-01-14 until 2022-01-21 the value of 2</li>
              <li>From 2022-01-21 until 2022-01-28 the value of 3</li>
              <li>From 2022-01-28 until 2022-02-01 the value of 4</li>
              <li>From 2022-02-01 until the end of time the value of 'Not Valid'</li>
            </ul>
          </div>
          <br />
          <div>
            Consider temporal-years-since and temporal-months-since for similar functionality on a different time
            factor.
          </div>
          <br />
          <div>
            Caution! a long period between the from and to can cause performance issues. Consider if you actually need
            weeks since and can't solve another way
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-days-since",
    arguments: [ Argument.required("from", "datetime"), Argument.required("to", "datetime") ],
    displayName: "Temporal Days Since",
    //definition: [/^every day between (.+) and (.+)/],
    description: `<div>
          <div>Create a temporal value that counts the number of days between two points (from and to)</div>
          <br />
          <div>
            Eg: temporal-days-since(2022-01-01, 2022-01-10) would produce a temporal value that counts the number of
            days from 1990 until 2020, like:
            <ul>
              <li>From the beginning of time until 2022-01-01 the value of 'Not Valid'</li>
              <li>From 2022-01-01 until 2022-01-02 the value of 0</li>
              <li>From 2022-01-02 until 2022-01-03 the value of 1</li>
              <li>From 2022-01-03 until 2022-01-04 the value of 2</li>
              <li>...</li>
              <li>From 2022-01-09 until 2022-01-10 the value of 9</li>
              <li>From 2022-01-09 until the end of time the value of 'Not Valid'</li>
            </ul>
          </div>
          <br />
          <div>
            Consider temporal-years-since and temporal-months-since for similar functionality on a different time
            factor.
          </div>
          <br />
          <div>
            Caution! a long period between the from and to can cause performance issues. Consider if you actually need
            days since and can't solve another way
          </div>
        </div>`,
  }),
  new Command({
    id: "temporal-day-of-the-week",
    arguments: [
      Argument.required("dayOfWeek", "string"),
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
    ],
    displayName: "Temporal Day of the Week",
  }),
  /*new Command({
    id: "temporal-interval-on-day-of-the-week",
    arguments: [
      Argument.required("fromTime", "time"),
      Argument.required("toTime", "time"),
      Argument.required("dayOfWeek", "string"),
      Argument.required("fromDate", "date"),
      Argument.required("toDate", "date"),
    ],
    displayName: "Temporal Interval On Day Of The Week",
  }),*/
  new Command({
    id: "temporal-interval-on-day-cycle",
    arguments: [
      Argument.required("fromTime", "time"),
      Argument.required("toTime", "time"),
      Argument.required("fromDate", "date"),
      Argument.required("toDate", "date"),
      Argument.required("cycleLength", "number"),
    ],
    displayName: "Temporal Interval On Day Cycle",
    /*description: `<div>
          <div>Create a temporal value that is true between set times within set dates. The dates are modified by an initial offset from the start date, and then the cycle to repeat</div>
          <br />
          <div>
            Eg: temporal-interval-on-day-cycle(9, 13, 2024-01-01, 2024-01-08, 2, 1) would produce a temporal value that produces:
            <ul>
              <li>From the beginning of time until 2024-01-01 the value of uncertain</li>
              <li>From 2024-01-01 until 2024-01-02 09:00:00 the value of false</li>
              <li>From 2024-01-02 09:00:00 until 2024-01-02 13:00:00 the value of true</li>
              <li>From 2024-01-02 13:00:00 until 2024-01-04 09:00:00 the value of false</li>
              <li>From 2024-01-04 9:00:00 until 2024-01-04 13:00:00 the value of true</li>
              <li>From 2024-01-04 13:00:00 until 2024-01-06 09:00:00 the value of false</li>
              <li>From 2024-01-06 09:00:00 until 2024-01-06 13:00:00 the value of true</li>
              <li>From 2024-01-06 13:00:00 until 2024-01-09 the value of false</li>
              <li>From 2024-01-09 until the end of time the value of uncertain</li>
            </ul>
          </div>
          <br />
        </div>`*/
  }),
  new Command({
    id: "interval-count-if",
    arguments: [
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
      //Argument.required("unit", "string"),
      Argument.required("condition", "boolean"),
    ],
    displayName: "Interval Count If",
  }),
  new Command({
    id: "interval-sum-if",
    arguments: [
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
      //Argument.required("unit", "string"),
      Argument.required("value", "auto"),
      Argument.required("condition", "boolean"),
    ],
    displayName: "Interval Sum If",
  }),
  new Command({
    id: "temporal-weekday",
    arguments: [ Argument.required("from", "datetime"), Argument.required("to", "datetime") ],
    //definition: [/^every week day between (.+) and (.+)/],
    displayName: "Temporal Weekday",
    description: `<div>
          <div>
            Creates a temporal range between from and to where the weekdays are true, and the weekends are false.
          </div>
          <br />
          <div>
            Eg: temporalWeekday(2022-01-01, 2022-01-14) would produce:
            <ul>
              <li>From the beginning of time until 2022-01-01 the value of 'Not Valid'</li>
              <li>From 2022-01-01 until 2022-01-02 the value of true</li>
              <li>From 2022-01-02 until 2022-01-04 the value of false</li>
              <li>From 2022-01-04 until 2022-01-09 the value of true</li>
              <li>From 2022-01-09 until 2022-01-11 the value of false</li>
              <li>From 2022-01-11 until 2022-01-14 the value of true</li>
              <li>From 2022-01-14 until the end of time the value of 'Not Valid'</li>
            </ul>
          </div>
          <br />
          <br />
          <div>
            Caution! a long period between the from and to can cause performance issues. Consider if you actually need
            this function and can't solve another way
          </div>
        </div>`,
  }),
  new Command({
    id: "divide",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [
      ///(.+) divide (.+)$/,
      ...createMathSymbolRegexes("/"),
    ],
    displayName: "Divide",
    description: `<div>
          <div>
            Returns the result of dividing 'b' from 'a'. Note: you can also use the alternative form 'a /
            b'
          </div>
          <br />
          <div>Eg: the payment amount / 10 would return the value of dividing the payment amount by 10.</div>
          <br />
        </div>`,
  }),
  new Command({
    id: "multiply",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [
      ///^(.+) multiply (.+)$/,
      ...createMathSymbolRegexes("*"),
    ],
    displayName: "Multiply",
    description: `<div>
          <div>
            Returns the result of multiplying 'a' and 'b' together. Note: you can also use the alternative
            form 'a * b'
          </div>
          <br />
          <div>Eg: multiply(the payment amount, 10) would return the value of multiplying 10 and the payment amount</div>
          <br />
        </div>`,
  }),

  /*new Command({
    id: "modulo",
    arguments: [Argument.required("a", "auto"), Argument.required("b", "auto")],
    definition: [/(.+) % (.+)$/],
    displayName: "Modulo",
  }),*/

  new Command({
    id: "plus",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    naturalPriority: 5,
    definition: [
      ///^(.+) plus (.+)$/,
      // Greedy first group so we match at the rightmost + (left-to-right evaluation)
      /(.*)\s*\+\s*(.+)$/,
    ],
    displayName: "Plus",
    description: `<div>
          <div>
            Returns the result of adding 'a' and 'b' together. Note: you can also use the alternative form
            'a + b'
          </div>
          <br />
          <div>Eg: plus(the payment amount, 10) would return the value of adding 10 to the payment amount</div>
          <br />
        </div>`,
  }),
  new Command({
    id: "minus",
    naturalPriority: 5,
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [
      ///^(.+) minus (.+)$/,
      /(.+) - (.+)$/,
      // en dash
      /(.+) – (.+)$/,
    ],
    displayName: "Minus",
    description: `<div>
          <div>
            Returns the result of subtracting 'b' from 'a'. Note: you can also use the alternative form
            'a - b'
          </div>
          <br />
          <div>Eg: minus(the payment amount, 10) would return the value of subtracting 10 from the payment amount</div>
          <br />
        </div>`,
  }),
  new Command({
    id: "modulo",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [],
    alias: [ "remainder" ],
    displayName: "Remainder",
    description: `<div>
          <div>
            Returns the remainder left over when 'a' is divided by 'b'. It always take the sign of 'a'
          </div>
          <br />
          <div>
            Eg: remainder(the payment amount, 6) would return 4 if the payment amount was 10 (as 10/6 leaves a remainder of
            4). If the 6 was negaged (eg: -6) the result would be the same. However, if the payment amount was negative
            it would return -4.
          </div>
          <br />
        </div>`,
  }),
  new Command({
    id: "trunc",
    arguments: [ Argument.required("value", "auto"), Argument.required("to", "auto") ],
    displayName: "Truncate",
    //definition: [/^(.+) truncated to (.+) decimal place$/, /(.+) truncated to (.+) decimal places$/],
    description: `<div>
          <div>Truncate the 'value' to 'to' decimal places. This effectively forces the value to be round down</div>
          <br />
          <div>Eg: trunc(10.678, 2) would return 10.67.</div>
          <br />
          <div>Use round for normal rounding style rules</div>
        </div>`,
  }),
  new Command({
    id: "round",
    arguments: [ Argument.required("value", "auto"), Argument.required("to", "auto") ],
    //definition: [/^(.+) rounded to (.+) decimal place$/, /(.+) rounded to (.+) decimal places$/],
    displayName: "Round",
    description: `<div>
          <div>
            Round the 'value' to 'to' decimal places. This will round up or down depending on the value (with .5
            rounding up)
          </div>
          <br />
          <div>Eg: round(10.678, 2) would return 10.68 and round(10.674, 2) would return 10.67.</div>
          <br />
          <div>Use trunc to force rounding down</div>
        </div>`,
  }),
  new Command({
    id: "round-up",
    arguments: [ Argument.required("value", "auto"), Argument.required("to", "auto") ],
    displayName: "Round Up",
    description: `<div>
          <div>
            Round the 'value' to 'to' decimal places away from zero.
          </div>
          <br />
          <div>Eg: round-up(10.671, 2) returns 10.68 and round-up(-10.671, 2) returns -10.68.</div>
          <br />
          <div>Use round for midpoint rounding and trunc to force rounding toward zero</div>
        </div>`,
  }),
  /*new Command({
    id: "round-down",
    arguments: [ Argument.required("value", "auto"), Argument.required("to", "auto") ],
    displayName: "Round Down",
    description: `<div>
          <div>
            Round the 'value' to 'to' decimal places toward negative infinity.
          </div>
          <br />
          <div>Eg: round-down(10.671, 2) returns 10.67 and round-down(-10.671, 2) returns -10.68.</div>
          <br />
          <div>Use round for midpoint rounding and trunc to force rounding toward zero</div>
        </div>`,
  }),*/
  new Command({
    id: "xy",
    arguments: [ Argument.required("x", "auto"), Argument.required("y", "auto") ],
    definition: [ /^(.*?) to the power of (.+)$/ ],
    displayName: "X to the Power of Y",
    description: `<div>
          <div>
            Returns x to the power of y
          </div>
          <br />
          <div>
            Eg: xy(2, 3) would produce 8
          </div>
        </div>`,
  }),
  new Command({
    id: "not-equals",
    returnType: "boolean",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [
      /^(.*?)<>(.+)$/,
      ///(.+) does not equal (.+)$/
    ],
    naturalPriority: CONDITION_PRIORITY + 5,
    displayName: "Not Equals",
    description: `<div>
            Returns true if the provided values do not match (are not the same). Note: you can also use the alternative
            form 'a &lt;&gt; b'
          </div>
          <br />
          <div>
            Eg: not-equals(the payment amount, the payment threshold) would return true both of these attributes were
            not the same value.
          </div>
          <br />
          <div>Consider equals for the opposite behaviour</div>
        </div>`,
  }),
  new Command({
    id: "less-than-equals",
    returnType: "boolean",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [
      ///^(.+) is less than or equal to (.+)$/,
      ...createSymbolRegexes("<="),
    ],
    naturalPriority: CONDITION_PRIORITY,
    displayName: "Less Than or Equals",
    description: `<div>
          <div>
            Returns true if the first argument is less than or equal to the second argument. You can provide values or
            attributes as these arguments. Alternatively you can use the more natural form &lt;value&gt; &lt;=
            &lt;value&gt;
          </div>
          <br />
          <div>
            Eg: less-than-equals(the persons age, 10) would return true if the person's age was less than or equal to 10
          </div>
          <br />
          <div>
            If either value is Uncertain, then the result will be Uncertain. If either value is Unknown, then the result
            will be Unknown.
          </div>
        </div>`,
  }),
  new Command({
    displayName: "Less Than",
    returnType: "boolean",
    id: "less-than",
    naturalPriority: CONDITION_PRIORITY,
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [
      ///^(.+) is less than (.+)$/,
      ...createSymbolRegexes("<"),
    ],
    description: `<div>
          <div>
            Returns true if the first argument is less than the second argument. You can provide values or attributes as
            these arguments. Alternatively you can use the more natural form &lt;value&gt; &lt; &lt;value&gt;
          </div>
          <br />
          <div>Eg: less-than(the persons age, 10) would return true if the person's age was less than 10</div>
          <br />
          <div>
            If either value is Uncertain, then the result will be Uncertain. If either value is Unknown, then the result
            will be Unknown.
          </div>
        </div>`,
  }),

  new Command({
    id: "greater-than-equals",
    returnType: "boolean",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    naturalPriority: CONDITION_PRIORITY,
    definition: [
      ///^(.+) is greater than or equal to (.+)$/,
      ...createSymbolRegexes(">="),
    ],
    description: `<div>
          <div>
            Returns true if the first argument is greater than or equal to the second argument. You can provide values
            or attributes as these arguments. Alternatively you can use the more natural form &lt;value&gt; &gt;=
            &lt;value&gt;
          </div>
          <br />
          <div>
            Eg: greater-than-equals(the persons age, 10) would return true if the person's age was greater than or equal
            to 10
          </div>
          <br />
          <div>
            If either value is Uncertain, then the result will be Uncertain. If either value is Unknown, then the result
            will be Unknown.
          </div>
        </div>`,
    displayName: "Greater Than or Equals",
  }),
  new Command({
    id: "greater-than",
    returnType: "boolean",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [
      ///^(.+) is greater than (.+)$/,
      ...createSymbolRegexes(">"),
    ],
    naturalPriority: CONDITION_PRIORITY,
    displayName: "Greater Than",
    description: `<div>
          <div>
            Returns true if the first argument is greater than the second argument. You can provide values or attributes
            as these arguments. Alternatively you can use the more natural form &lt;value&gt; &gt; &lt;value&gt;
          </div>
          <br />
          <div>Eg: greater-than(the persons age, 10) would return true if the person's age was greater than 10</div>
          <br />
          <div>
            If either value is Uncertain, then the result will be Uncertain. If either value is Unknown, then the result
            will be Unknown.
          </div>
        </div>`,
  }),

  new Command({
    id: "known",
    arguments: [ Argument.required("value", "auto") ],
    definition: [ /^(.+) is known/ ],
    description: `<div>
          <div>Returns true if the value of the attribute is known or uncertain, otherwise unknown.</div>
          <br />
          <div>
            Eg: known(the persons age) would return true if the person's age was 10 or uncertain, but false if it was
            unknown
          </div>
          <br />
          <div>
            Consider the use of

            <em>certain</em>

            if you need to differentiate if the value is uncertain
          </div>
        </div>`,
    displayName: "Known",
  }),
  new Command({
    id: "unknown",
    arguments: [ Argument.required("value", "auto") ],
    definition: [ /^(.+) is unknown/ ],
    displayName: "Unknown",
    description: `<div>
          <div>
            Returns true if the value of the attribute is unknown, otherwise true for any other value (including
            uncertain).
          </div>
          <br />
          <div>Eg: unknown(the persons age) would return true only if the person's age was unknown</div>
        </div>`,
  }),
  new Command({
    id: "instance-sum",
    arguments: [ Argument.required("relationship", "relationship"), Argument.required("attribute", "auto") ],
    displayName: "Instance Sum",
    //definition: [/^the sum of (?<$2>.+?) over (?<$1>.+)$/],
    description: `<div>
          <div>Sums the 'attribute' over all instances of the 'relationship'</div>
          <br />
          <div>
            Eg: instance-sum(all instances of the payment, the payment amount) would return the total of adding all
            payment amounts for each instance together.
          </div>
          <br />
          <div>Consider instance-sum-if if you need to apply conditional logic on which instances to sum</div>
        </div>`,
  }),
  new Command({
    id: "instance-sum-if",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    //definition: [/^the sum of (?<$2>.+?) over (?<$1>.+) where (?<$3>.+)$/],
    displayName: "Instance Sum If",
    description: `<div>
          <div>
            Sums the 'sum attribute' over all instances of the 'relationship' where the 'condition' on that instance is
            true
          </div>
          <br />
          <div>
            Eg: instance-sum(all instances of the payment, the payment amount, the payment amount &gt; 10) would return
            the total of adding all payment amounts for each instance together where the payment amount is greater than
            10
          </div>
          <br />
          <div>Consider instance-sum if you just want a sum of all instances</div>
        </div>`,
  }),
  new Command({
    id: "instance-count-if",
    arguments: [ Argument.required("relationship", "relationship"), Argument.required("condition", "boolean") ],
    definition: [ /^the number of (.+) for which it is case that (.+)/ ],
    displayName: "Instance Count If",
    description: `<div>
          <div>
            Counts the number of instances of the provided relationship where the 'condition' on that instance is true
          </div>
          <br />
          <div>
            Eg: instance-count-if(all instances of the payment, the payment amount &gt; 10) would return the number of
            payment instances within the graph that have a payment amount greater than 10
          </div>
          <br />
          <div>Consider instance-count if you just want a count of all instances</div>
        </div>`,
  }),
  new Command({
    id: "instance-count-if-alias",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("alias", "alias"),
      Argument.required("condition", "boolean"),
    ],
    definition: [],
    displayName: "Instance Count If Alias",
  }),
  new Command({
    id: "instance-count",
    arguments: [ Argument.required("relationship", "relationship") ],
    definition: [ /^the number of (.+)/ ],
    displayName: "Instance Count",
    description: `<div>
          <div>Counts the number of instances of the provided relationship</div>
          <br />
          <div>
            Eg: instance-count(all instances of the payment) would return the number of payment instances within the
            graph.
          </div>
          <br />
          <div>Consider instance-count-if if you need to apply conditional logic on which instances to count</div>
        </div>`,
  }),
  new Command({
    id: "instance-maximum-if",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    //definition: [/^the maximum (?<$2>.+) over (?<$1>.+) where (?<$3>.+)/],
    displayName: "Instance Maximum If",
    description: `<div>
          <div>
            Find the greatest (max) 'attribute' over all instances of the 'relationship' where the 'condition' on that
            instance is true
          </div>
          <br />
          <div>
            Eg: instance-maximum(all instances of the payment, the payment amount, the payment amount &gt; 10) would
            return the greatest payment amount, greater than 10, across all instances.
          </div>
          <br />
          <div>Consider instance-maximum if you need the maximum of all instances</div>
        </div>`,
  }),
  new Command({
    id: "instance-maximum-if-alias",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("alias", "alias"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    definition: [],
    displayName: "Instance Maximum If Alias",
  }),
  new Command({
    id: "instance-maximum",
    arguments: [ Argument.required("relationship", "relationship"), Argument.required("attribute", "auto") ],
    //definition: [/^the maximum (?<$2>.+) over (?<$1>.+)/],
    displayName: "Instance Maximum",
    description: `<div>
          <div>Find the greatest (max) 'attribute' over all instances of the 'relationship'.</div>
          <br />
          <div>
            Eg: instance-maximum(all instances of the payment, the payment amount) would return the greatest payment
            amount across all instances.
          </div>
          <br />
          <div>
            Consider instance-maximum-if if you need to apply conditional logic on which instances to include the
            maximum function
          </div>
        </div>`,
  }),
  new Command({
    id: "instance-minimum-if",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    displayName: "Instance Minimum If",
    //definition: [/^the minimum (?<$2>.+) over (?<$1>.+) where (?<$3>.+)/],
    description: `<div>
          <div>
            Find the lowest 'attribute' over all instances of the 'relationship' where the 'condition' on that
            instance is true
          </div>
          <br />
          <div>
            Eg: instance-minimum(all instances of the payment, the payment amount, the payment amount &gt; 10) would
            return the lowest payment amount, greater than 10, across all instances.
          </div>
          <br />
          <div>Consider instance-minimum if you need the minimum of all instances</div>
        </div>`,
  }),
  new Command({
    id: "instance-minimum-if-alias",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("alias", "alias"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    definition: [],
    displayName: "Instance Minimum If Alias",
  }),
  new Command({
    id: "instance-minimum",
    arguments: [ Argument.required("relationship", "relationship"), Argument.required("attribute", "auto") ],
    //definition: [/^the minimum (?<$2>.+) over (?<$1>.+)/],
    displayName: "Instance Minimum",
    description: `<div>
          <div>Find the lowest 'attribute' over all instances of the 'relationship'.</div>
          <br />
          <div>
            Eg: instance-minimum(all instances of the payment, the payment amount) would return the lowest payment
            amount across all instances.
          </div>
          <br />
          <div>
            Consider instance-minimum-if if you need to apply conditional logic on which instances to include the
            minimum function
          </div>
        </div>`,
  }),
  new Command({
    id: "instance-value-if",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    definition: [],
    displayName: "Instance Value If",
    description: `<div>
          <div>
            Finds the one value of the 'attribute' across all instances of the 'relationship' for which the
            'condition' is true. If multiple instances return true for the 'attribute', then the result will be
            Uncertain.
          </div>
          <br />
          <div>
            Eg: instance-value-if(all instances of the payment, the payment amount, the payment amount &gt; 10) would
            return the one payment amount that was greater than 10. If there were multiple, this would return uncertain.
          </div>
          <br />
        </div>`,
  }),
  new Command({
    id: "instance-value-if-alias",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("alias", "alias"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    definition: [],
    displayName: "Instance Value If Alias",
  }),
  FOR_COMMAND,
  new Command({
    id: "for-all",
    arguments: [ Argument.required("relationship", "relationship"), Argument.required("condition", "boolean") ],
    definition: [ /^for each of (.+), (.+)$/ ],
    displayName: "For All",
    description: `<div>
          <div>Returns true if all instances of 'relationship' have the 'condition' returning true.</div>
          <br />
          <div>
            Eg: exists(all instances of the payment, the payment amount &gt; 10) would return true if all payments were
            greater than 10.
          </div>
          <br />
          <div>
            Consider exists if you want to check that there is at least one instance meets the condition, allowing some
            instances to not meet the condition.
          </div>
        </div>`,
  }),
  new Command({
    id: "for-all-scope",
    arguments: [ Argument.required("relationship", "relationship"), Argument.optional("alias", "alias") ],
    definition: [],
    displayName: "For All Scope",
  }),
  new Command({
    id: "exists",
    arguments: [ Argument.required("relationship", "relationship"), Argument.required("condition", "boolean") ],
    definition: [ /^for at least one of (.+), (.+)$/ ],
    displayName: "Exists",
    description: `<div>
          <div>Returns true if there is at least one instance of 'relationship' where the 'condition' is true.</div>
          <br />
          <div>
            Eg: exists(all instances of the payment, the payment amount &gt; 10) would return true if there was at least
            one payment greater than 10.
          </div>
          <br />
          <div>Consider for-all if you need to return true if all instances meet the condition.</div>
        </div>`,
  }),
  new Command({
    id: "exists-scope",
    arguments: [ Argument.required("relationship", "relationship"), Argument.optional("alias", "alias") ],
    displayName: "Exists Scope",
    definition: [],
    description: `<div>
          <div>
            Check to see if any member of a relationship satisfies rule conditions. Where an alias is specified, the rule conditions can be used to compare one instance of the target entity to another.
          </div>
          <br />
          <div>
            Eg: the person has an adult female child = existsScope(all instances of the child for the person). Amongst all the children at least one instance of the child that is a member of the relationship is both female and an adult.
          </div>
          <br />
          <div>
            Eg: the child is not an eldest child child = existsScope(all instances of the child for the person, the other child).<br/>the child's age &lt; the other child's age
            <br/>Checks whether a given child is an eldest child by comparing the ages of the child against the ages of all the children that are members of the relationship.
          </div>
        </div>`,
  }),
  AND_COMMAND,
  OR_COMMAND,
  new Command({
    id: "uncertain",
    arguments: [ Argument.optional("value", "auto") ],
    definition: [ /(.+) is uncertain$/ ],
    description: `<div>
          <div>
            Returns true if the value of the attribute is uncertain, otherwise false for any known value or unknown if
            the attribute is unknown.
          </div>
          <br />
          <div>Eg: uncertain(the persons age) would return true if the person's age was uncertain.</div>
          <br />
        </div>`,
    displayName: "Uncertain",
  }),
  new Command({
    id: "upper",
    arguments: [ Argument.required("string", "string") ],
    definition: [
      ///^(.+) in upper case/
    ],
    displayName: "Upper",
    description: `<div>
          <div>Returns the provided 'value' in upper case</div>
          <br />
          <div>Eg: upper("the VaLue") would return "THE VALUE"</div>
          <br />
          <div>See lower for the opposite behaviour</div>
        </div>`,
  }),
  new Command({
    id: "lower",
    arguments: [ Argument.required("string", "string") ],
    definition: [
      ///^(.+) in lower case/
    ],
    displayName: "Lower",
    description: `<div>
          <div>Returns the provided 'value' in lower case</div>
          <br />
          <div>Eg: lower("the VaLue") would return "the value"</div>
          <br />
          <div>See upper for the opposite behaviour</div>
        </div>`,
  }),
  new Command({
    id: "substring",
    arguments: [
      Argument.required("string", "string"),
      Argument.required("start", "number"),
      Argument.optional("length", "number"),
    ],
    definition: [],
    displayName: "Substring",
    description: `<div>
          <div>
            Returns the portion of the provided 'string' starting at 'start' for 'length' characters (inclusive of
            spaces, punctuation etc).
          </div>
          <br />
          <div>Eg: substring('test', 0, 2) would return 'te'</div>
        </div>`,
  }),
  new Command({
    id: "contains",
    arguments: [ Argument.required("string", "string"), Argument.required("find", "string") ],
    definition: [
      ///^(.+) contains (.+)/
    ],
    displayName: "Contains",
    description: `<div>
          <div>
            Returns true if the string contains the find string. This is case insensitive.
          </div>
          <br />
          <div>
            Eg: contains('Rules Platform', 'rules') would return true
            <br />
            As would contains('Rules Platform', 'Rules') - as the search is case insensitive
          </div>
        </div>`,
  }),
  new Command({
    id: "starts-with",
    arguments: [ Argument.required("string", "string"), Argument.required("find", "string") ],
    definition: [
      ///^(.+) contains (.+)/
    ],
    displayName: "StartsWith",
    description: `<div>
          <div>
            Returns true if the string starts with the find string. This is case insensitive.
          </div>
          <br />
          <div>
            Eg: starts-with('Rules Platform', 'rules') would return true
            <br />
            As would starts-with('Rules Platform', 'Rules') - as the search is case insensitive
          </div>
        </div>`,
  }),
  new Command({
    id: "ends-with",
    arguments: [ Argument.required("string", "string"), Argument.required("find", "string") ],
    definition: [
      ///^(.+) contains (.+)/
    ],
    displayName: "EndsWith",
    description: `<div>
          <div>
            Returns true if the string ends with the find string. This is case insensitive.
          </div>
          <br />
          <div>
            Eg: ends-with('Rules Platform', 'platform') would return true
            <br />
            As would ends-with('Rules Platform', 'Platform') - as the search is case insensitive
          </div>
        </div>`,
  }),
  new Command({
    id: "matches",
    arguments: [ Argument.required("string", "string"), Argument.required("regex", "string") ],
    definition: [],
    displayName: "Matches",
  }),
  new Command({
    id: "length",
    arguments: [ Argument.required("string", "string") ],
    definition: [
      ///^the length of (.+)/
    ],
    displayName: "Length",
  }),
  NOT_COMMAND,
  new Command({
    id: "certain",
    arguments: [ Argument.required("value", "auto") ],
    definition: [ /^(.+) is certain$/ ],
    description: `<div>
          <div>Returns true if the value of the attribute is known, false if uncertain, and otherwise unknown.</div>
          <br />
          <div>
            Eg: certain(the persons age) would return true if the person's age was 10, but false if it was uncertain.
          </div>
          <br />
          <div>
            Consider the use of

            <em>known</em>

            if you want to return true if the value is uncertain
          </div>
        </div>`,
    displayName: "Certain",
  }),
  new Command({
    id: "negate",
    arguments: [ Argument.required("value", "auto") ],
    definition: [
      ///^the negative of (.+)/,
      /^-(.+)$/,
    ],
    displayName: "Negate",
    description: `<div>
          <div>
            Returns the opposite of the condition. Note: this works for more conditions than not, so allowing the
            opposite of a number (10 = -10), a boolean (true = false), otherwise Uncertain.
          </div>
          <br />
          <div>Eg: negate(the customer's rent) would return -10 if the customer's rent is 10</div>
          <br />
          <div>Consider not() if only want to negate booleans</div>
        </div>`,
  }),
  new Command({
    id: "abs",
    definition: [
      ///^the absolute value of (.+)/
    ],
    arguments: [ Argument.required("value", "number") ],
    displayName: "Absolute Value",
    description: `<div>
          <div>
            Returns the absolute value of a number.
          </div>
          <br />
          <div>
            Eg: abs(-3) would produce 3
          </div>
        </div>`,
  }),

  new Command({
    id: "interval-always",
    arguments: [
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
      Argument.required("range", "boolean"),
    ],
    displayName: "Interval Always",
    definition: [
      ///^between (.+) and (.+) the value of (.+) is always true/
    ],
    description: `<div>
          <div>Determine if between 'from' and 'to' the provided temporal 'range' is true</div>
          <br />
          <div>
            Eg: interval-always(2022-01-01, 2022-03-01, the customer is approved) would return true if the customer is
            approved is true between the two dates. If any date within this range was not true, then the result would be
            false.
          </div>
          <br />
          <div>
            Consider interval-at-least-days, interval-consecutive-days or interval-sometimes for alternative
            restrictions on the period
          </div>
        </div>`,
  }),
  new Command({
    id: "interval-at-least-days",
    arguments: [
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
      Argument.required("period", "number"),
      Argument.required("range", "boolean"),
    ],
    displayName: "Interval At Least Days",
    definition: [
      ///^between (.+) and (.+) there is atleast (.+) days where (.+) is true/
    ],
    description: `<div>
          <div>
            Determine if between 'from' and 'to' there is a atleast 'period' days in the temporal range that is true.
            These periods of true do not need to be consecutive
          </div>
          <br />
          <div>
            Eg: interval-at-least-days(2022-01-01, 2022-03-01, 7, the customer is approved) would return true if the
            customer is approved is true between the two dates for atleast 7 days.
          </div>
          <br />
          <div>
            Consider interval-always-days, interval-consecutive-days or interval-sometimes for alternative restrictions
            on the period
          </div>
        </div>`,
  }),
  new Command({
    id: "interval-consecutive-days",
    arguments: [
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
      Argument.required("period", "number"),
      Argument.required("range", "boolean"),
    ],
    displayName: "Interval Consecutive Days",
    //definition: [/^between (.+) and (.+) there is a (.+) day consecutive period where (.+) is true/],
    description: `<div>
          <div>
            Determine if between 'from' and 'to' there is a 'period' of consecutive days in the temporal range that is
            true. These periods must be consecutive.
          </div>
          <br />
          <div>
            Eg: interval-consecutive-days(2022-01-01, 2022-03-01, 7, the customer is approved) would return true if the
            customer is approved is true between the two dates for a period of 7 consecutive days. If there was a period
            that was 6 days and another for 3 days the result would still be false, as the period must be consecutive
          </div>
          <br />
          <div>
            Consider interval-always-days, interval-at-least-days or interval-sometimes for alternative restrictions on
            the period
          </div>
        </div>`,
  }),
  new Command({
    id: "interval-sometimes",
    arguments: [
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
      Argument.required("range", "boolean"),
    ],
    //definition: [/^between (.+) and (.+) there is any period where (.+) is true/],
    displayName: "Interval Sometimes",
    description: `<div>
          <div>
            Determine if between 'from' and 'to' there is any period that is true. This period can be less than a day.
          </div>
          <br />
          <div>
            Eg: interval-sometimes(2022-01-01, 2022-03-01, the customer is approved) would return true if the
            customer is approved is true between the two dates at any time for any length.
          </div>
          <br />
          <div>
            Consider interval-always-days, interval-at-least-days or interval-consecutive-days for alternative
            restrictions on the period
          </div>
        </div>`,
  }),
  new Command({
    id: "interval-daily-sum",
    arguments: [
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
      Argument.required("value", "auto"),
    ],
    //definition: [/^the sum of (?<$3>.+) between (?<$1>.+) and (?<$2>.+)/],
    displayName: "Interval Daily Sum",
    description: `<div>
          <div>Between 'from' and 'to' calculate the number of days and then multiply by the 'value'</div>
          <br />
          <div>Eg: interval-daily-sum(2022-01-01, 2022-01-10, 10) would return 100 (10 days x 10).</div>
          <br />
          <div>Consider interval-daily-sum-if if you need a restriction on which days to include</div>
        </div>`,
  }),
  new Command({
    id: "interval-daily-sum-if",
    arguments: [
      Argument.required("from", "datetime"),
      Argument.required("to", "datetime"),
      Argument.required("value", "auto"),
      Argument.required("condition", "boolean"),
    ],
    //definition: [/^the sum of (.+) where (.+) is true between (.+) and (.+)/],
    displayName: "Interval Daily Sum If",
    description: `<div>
          <div>
            Between 'from' and 'to' calculate the number of days and then multiply by the 'value', but only where the
            'condition' resolves to true.
          </div>
          <br />
          <div>
            Eg: interval-daily-sum-if(2022-01-01, 2022-01-10, 10, the customer is approved) would return the days where
            the customer is approved multiplied by 10.
          </div>
        </div>`,
  }),
  new Command({
    id: "instance-sum-if-alias",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("alias", "alias"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    definition: [],
    displayName: "Instance Sum If Alias",
  }),
  new Command({
    id: "temporal-on",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^a value that is true on (.+)/],
    description: `<div>
          <div>
            Create a temporal range that is true on the provided date (from the beginning until the end of the day).
          </div>
          <br />
          <div>
            Eg: temporal-on(the payment date) would create a temporal value that is true from midnight of the payment
            date for 24 hours.
          </div>
          <br />
          <div>
            Consider the temporal-before or temporal-after functions to create temporal values that span from a date
            until the beginning/end of time
          </div>
        </div>`,
    displayName: "Temporal On",
  }),
  new Command({
    id: "maximum",
    arguments: [
      Argument.required("a", "auto"),
      Argument.required("b", "auto", {
        variadic: true,
      }),
    ],
    definition: [ /^the maximum of (.+) and (.+)$/, /^the latest of (.+) and (.+)$/ ],
    displayName: "Maximum",
    description: `<div>
          <div>Returns the maximum of 'a' and 'b'.</div>
          <br />
          <div>
            Eg: maximum(the payment amount, 10) would return 10 if the payment amount was less than 10, or otherwise it
            would return the payment amount
          </div>
          <br />
        </div>`,
  }),
  new Command({
    id: "minimum",
    arguments: [
      Argument.required("a", "auto"),
      Argument.required("b", "auto", {
        variadic: true,
      }),
    ],
    definition: [ /^the minimum of (.+) and (.+)$/, /^the earliest of (.+) and (.+)$/ ],
    displayName: "Minimum",
    description: `<div>
          <div>Returns the minimum of 'a' and 'b'.</div>
          <br />
          <div>
            Eg: minimum(the payment amount, 10) would return the payment amount if the payment amount was less than 10,
            or otherwise it would return 10
          </div>
          <br />
        </div>`,
  }),
  new Command({
    id: "year-difference-inclusive",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    displayName: "Year Difference Inclusive",
    //definition: [/^the difference in years between (.+) and (.+) inclusive$/],
    description: `<div>
          <div>
            Return the difference in years (rounded down to the nearest whole number) between two dates. You can provide
            a date or a 4 digit year. Note: this function will return a negative number if the first date is greater
            than the second date.
          </div>
          <br />
          <div>Eg: year-difference(2015, 2020) would return 5.</div>
          <br />
          <div>Consider year-difference if you don't want to include the end year.</div>
        </div>`,
  }),
  new Command({
    id: "month-difference-inclusive",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in months between (.+) and (.+) inclusive$/],
    displayName: "Month Difference Inclusive",
    description: `<div>
          <div>
            Return the difference in months (rounded down to the nearest whole number) between two dates. Note: this
            difference is based on the month, not a 30 day period. Additionally, this function will return a negative
            number if the first date is greater than the second date.
          </div>
          <br />
          <div>Eg: month-difference(2020-01-01, 2020-06-01) would return 6.</div>
          <br />
          <div>Consider month-difference if you don't want to include the end month.</div>
        </div>`,
  }),
  new Command({
    id: "hour-difference-inclusive",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in hours between (.+) and (.+) inclusive$/],
    displayName: "Hour Difference Inclusive",
  }),
  new Command({
    id: "minute-difference-inclusive",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in minutes between (.+) and (.+) inclusive$/],
    displayName: "Minute Difference Inclusive",
  }),
  new Command({
    id: "second-difference-inclusive",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in seconds between (.+) and (.+) inclusive$/],
    displayName: "Second Difference Inclusive",
  }),
  new Command({
    id: "year-difference",
    displayName: "Year Difference",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in years between (.+) and (.+)$/],
    description: `<div>
          <div>
            Return the difference in years (rounded down to the nearest whole number) between two dates. You can provide
            a date or a 4 digit year. Note: this function will return a negative number if the first date is greater
            than the second date.
          </div>
          <br />
          <div>Eg: year-difference(2015, 2020) would return 4 (it's non-inclusive).</div>
          <br />
          <div>Consider year-difference-inclusive if you need to include the end year.</div>
        </div>`,
  }),
  new Command({
    displayName: "Month Difference",
    id: "month-difference",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in months between (.+) and (.+)$/],
    description: `<div>
          <div>
            Return the difference in months (rounded down to the nearest whole number) between two dates. Note: this
            difference is based on the month, not a 30 day period. Additionally, this function will return a negative
            number if the first date is greater than the second date.
          </div>
          <br />
          <div>Eg: month-difference(2020-01-01, 2020-06-01) would return 5 (it's non-inclusive).</div>
          <br />
          <div>Consider month-difference-inclusive if you need to include the end month.</div>
        </div>`,
  }),
  new Command({
    displayName: "Day Difference",
    id: "day-difference",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in days between (.+) and (.+)$/],
    description: `<div>
          <div>
            Return the difference in days (rounded down to the nearest whole number) between two dates. Note: this
            function will return a negative number if the first date is greater than the second date.
          </div>
          <br />
          <div>Eg: day-difference(2020-01-01, 2020-01-06) would return 5 (it's non-inclusive).</div>
          <br />
          <div>Consider day-difference-inclusive if you need to include the end day.</div>
        </div>`,
  }),
  new Command({
    displayName: "Hour Difference",
    id: "hour-difference",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in hours between (.+) and (.+)$/],
  }),
  new Command({
    displayName: "Minute Difference",
    id: "minute-difference",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in minutes between (.+) and (.+)$/],
  }),
  new Command({
    displayName: "Second Difference",
    id: "second-difference",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in seconds between (.+) and (.+)$/],
  }),
  new Command({
    id: "add-years",
    arguments: [ Argument.required("date", "datetime"), Argument.required("number", "number") ],
    definition: [ /^the date (?<$2>.+) years after (?<$1>.+)$/ ],
    displayName: "Add Years",
    description: `<div>
          <div>
            Add 'number' years to the provided 'date'. Pass a negative number to subtract (go backwards in time)
          </div>
          <br />
          <div>Eg: add-years(2020-01-01, 10) would return 2030-01-01.</div>
          <br />
          <div>Consider add-months, add-weeks, add-days, add-hours for alternative periods.</div>
        </div>`,
  }),
  new Command({
    id: "add-weeks",
    arguments: [ Argument.required("date", "datetime"), Argument.required("number", "number") ],
    definition: [ /^the date (?<$2>.+) weeks after (?<$1>.+)$/ ],
    displayName: "Add Weeks",
    description: `<div>
          <div>
            Add 'number' weeks to the provided 'date'. Pass a negative number to subtract (go backwards in time)
          </div>
          <br />
          <div>Eg: add-weeks(2020-01-01, 10) would return 2020-03-11.</div>
          <br />
          <div>Consider add-years, add-months, add-days, add-hours for alternative periods.</div>
        </div>`,
  }),
  new Command({
    id: "add-months",
    arguments: [ Argument.required("date", "datetime"), Argument.required("number", "number") ],
    definition: [ /^the date (?<$2>.+) months after (?<$1>.+)$/ ],
    displayName: "Add Months",
    description: `<div>
          <div>
            Add 'number' months to the provided 'date'. Pass a negative number to subtract (go backwards in time)
          </div>
          <br />
          <div>Eg: add-months(2020-01-01, 10) would return 2020-10-01.</div>
          <br />
          <div>Consider add-years, add-weeks, add-days, add-hours for alternative periods.</div>
        </div>`,
  }),
  new Command({
    id: "add-days",
    arguments: [ Argument.required("date", "datetime"), Argument.required("number", "number") ],
    definition: [ /^the date (?<$2>.+) days after (?<$1>.+)$/ ],
    displayName: "Add Days",
    description: `<div>
          <div>Add 'number' days to the provided 'date'. Pass a negative number to subtract (go backwards in time)</div>
          <br />
          <div>Eg: add-days(2020-01-01, 10) would return 2020-01-11.</div>
          <br />
          <div>Consider add-years, add-months, add-weeks, add-hours for alternative periods.</div>
        </div>`,
  }),
  new Command({
    id: "add-days-if",
    arguments: [
      Argument.required("date", "datetime"),
      Argument.required("number", "number"),
      Argument.required("filter", "boolean"),
    ],
    displayName: "Add Days If",
  }),
  new Command({
    id: "add-hours",
    arguments: [ Argument.required("date", "datetime"), Argument.required("number", "number") ],
    //definition: [/^(?<$2>.+) hours after (?<$1>.+)$/],
    displayName: "Add Hours",
    description: `<div>
          <div>
            Add 'number' hours to the provided 'date'. Pass a negative number to subtract (go backwards in time)
          </div>
          <br />
          <div>Eg: add-hours(2020-01-01 10:00:00, 10) would return 2020-01-01 20:00:00.</div>
          <br />
          <div>Consider add-years, add-months, add-days, add-weeks for alternative periods.</div>
        </div>`,
  }),
  new Command({
    id: "add-minutes",
    arguments: [ Argument.required("date", "datetime"), Argument.required("number", "number") ],
    //definition: [/^(?<$2>.+) minutes after (?<$1>.+)$/],
    displayName: "Add Minutes",
  }),
  new Command({
    id: "add-seconds",
    arguments: [ Argument.required("date", "datetime"), Argument.required("number", "number") ],
    //definition: [/^(?<$2>.+) seconds after (?<$1>.+)$/],
    displayName: "Add Seconds",
  }),
  new Command({
    id: "extract-year",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^the year of (.+)$/],
    displayName: "Extract Year",
    description: `<div>
          <div>Extract the year component from a date.</div>
          <br />
          <div>Eg: extract-year(2020-01-01) would return 2020</div>
          <br />
          <div>
            Consider extract-month, extract-day, extract-hour, extract-minute, extract-second, extract-time-of-day,
            extract-date for alternative periods.
          </div>
        </div>`,
  }),
  new Command({
    id: "extract-month",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^the month of (.+)$/],
    displayName: "Extract Month",
    description: `<div>
          <div>Extract the month component from a date.</div>
          <br />
          <div>Eg: extract-month(2020-02-01) would return 2</div>
          <br />
          <div>
            Consider extract-year, extract-day, extract-hour, extract-minute, extract-second, extract-time-of-day,
            extract-date for alternative periods.
          </div>
        </div>`,
  }),
  new Command({
    id: "extract-day",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^the day of (.+)$/],
    displayName: "Extract Day",
    description: `<div>
          <div>Extract the day component from a date.</div>
          <br />
          <div>Eg: extract-day(2020-02-01) would return 1</div>
          <br />
          <div>
            Consider extract-year, extract-month, extract-hour, extract-minute, extract-second, extract-time-of-day,
            extract-date for alternative periods.
          </div>
        </div>`,
  }),
  new Command({
    id: "extract-hour",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^the hour of (.+)$/],
    displayName: "Extract Hour",
    description: `<div>
          <div>Extract the hour component from a date time (all dates in the system are date times).</div>
          <br />
          <div>Eg: extract-hour(2020-02-01 10:30:15) would return 10</div>
          <br />
          <div>
            Consider extract-year, extract-month, extract-day, extract-minute, extract-second, extract-time-of-day,
            extract-date for alternative periods.
          </div>
        </div>`,
  }),
  new Command({
    id: "extract-minute",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^the minute of (.+)$/],
    displayName: "Extract Minute",
    description: `<div>
          <div>Extract the minute component from a date time (all dates in the system are date times).</div>
          <br />
          <div>Eg: extract-minute(2020-02-01 10:30:15) would return 30</div>
          <br />
          <div>
            Consider extract-year, extract-month, extract-day, extract-hour, extract-second, extract-time-of-day,
            extract-date for alternative periods.
          </div>
        </div>`,
  }),
  new Command({
    id: "extract-second",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^the second of (.+)$/],
    displayName: "Extract Second",
    description: `<div>
          <div>Extract the second component from a date time (all dates in the system are date times).</div>
          <br />
          <div>Eg: extract-hour(2020-02-01 10:30:15) would return 15</div>
          <br />
          <div>
            Consider extract-year, extract-month, extract-day, extract-hour, extract-minute, extract-time-of-day,
            extract-date for alternative periods.
          </div>
        </div>`,
  }),
  new Command({
    id: "extract-time-of-day",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^the time of day of (.+)$/],
    displayName: "Extract Time Of Day",
    description: `<div>
          <div>Extract the time component from a date time (all dates in the system are date times).</div>
          <br />
          <div>Eg: extract-time-of-day(2020-02-01 10:30:15) would return 10:30:15 (as a string)</div>
          <br />
          <div>
            Consider extract-year, extract-month, extract-day, extract-hour, extract-minute, extract-second,
            extract-date for alternative periods.
          </div>
        </div>`,
  }),
  new Command({
    id: "extract-date",
    arguments: [ Argument.required("date", "datetime") ],
    //definition: [/^the date of (.+)$/],
    displayName: "Extract Date",
    description: `<div>
          <div>Extract the date component from a date time (all dates in the system are date times).</div>
          <br />
          <div>Eg: extract-date(2020-02-01 10:30:15) would return 2020-02-01</div>
          <br />
          <div>
            Consider extract-year, extract-month, extract-day, extract-hour, extract-minute, extract-second,
            extract-time-of-day for alternative periods.
          </div>
        </div>`,
  }),
  new Command({
    id: "value-at",
    arguments: [ Argument.required("date", "datetime"), Argument.required("range", "auto") ],
    definition: [ /^the value of (.+) on (.+)$/ ],
    displayName: "Value At",
    description: `<div>
          <div>Returns the value of the temporal 'range' at the given 'date'</div>
          <br />
          <div>Eg: value-at(2020-01-01, the persons age) would the value of the persons age on 2020-01-01</div>
          <br />
          <div>Consider when-next if you are trying to find when the range was next true</div>
        </div>`,
  }),
  new Command({
    id: "when-next",
    arguments: [ Argument.required("date", "datetime"), Argument.required("range", "auto") ],
    //definition: [/^the first date on which (?<$2>.+) on or after (?<$1>.+)$/],
    displayName: "When Next",
    description: `<div>
          <div>Return the first day (inclusive) from 'date' where the temporal 'range' is true.</div>
          <br />
          <div>
            Eg: when-next(2020-01-01, the application is granted) would return '2020-01-01' if the application granted
            was:
            <ul>
              <li>Unknown from the beginning of time until 2019-01-01</li>
              <li>true from 2019-01-01 until 2023-01-01</li>
              <li>Unknown from 2023-01-01 until the end of time</li>
            </ul>
          </div>
          <br />
          <div>See value-at if you just want the value at a specific time point</div>
        </div>`,
  }),
  new Command({
    id: "next-day-of-the-week",
    arguments: [ Argument.required("date", "datetime"), Argument.required("day", "string") ],
    definition: [ /^the first (?<$2>.+) on or after (?<$1>.+)$/ ],
    displayName: "Next Day Of The Week",
    description: `<div>
          <div>Get the date of the next provided week day from the provided date.</div>
          <br />
          <div>
            Eg: next-day-of-the-week(2022-01-01, 'Monday') would return 2022-01-03, as that is the next Monday on or
            after the 2022-01-01. Note the calculation is inclusive, so next-day-of-the-week(2022-01-01, 'Saturday')
            would return 2022-01-01 as that date is a Saturday.
          </div>
          <br />
          <div>Note: the 'day' component must be the english form of the day, but is case insensitive.</div>
        </div>`,
  }),
  new Command({
    id: "when-last",
    arguments: [ Argument.required("date", "datetime"), Argument.required("range", "auto") ],
    //definition: [/^the latest date on which (?<$2>.+) on or before (?<$1>.+)$/],
    displayName: "When Last",
  }),
  new Command({
    id: "repeat",
    arguments: [ Argument.required("until", "boolean"), Argument.required("for", "number") ],
    definition: [ /^repeat until (.+) for a maximum of (.+) times$/ ],
    displayName: "Repeat",
    description: `<div>
          <div>
            Repeats the processing of provided attributes until some attribute is true. Alternatively you can use the more function form Repeat(&lt;until&gt;)
          </div>
          <br />
          <div>
            Eg: 'repeat until the loop is complete' would repeat until the loop is complete is set to true. Pass a series of input and outputs like so:
            <br />
            input = output
            <br />
            input = output

          </div>
          <br />
          <div>
            The 'until' parameter cannot be temporal
          </div>
        </div>`,
  }),
  new Command({
    id: "concatenate-datetime",
    alias: [ "concatenate-date-time" ],
    arguments: [ Argument.required("date", "date"), Argument.required("time", "time") ],
    displayName: "Concatenate Date Time",
    definition: [ /^the combination of the date (.+) and the time (.+)$/ ],
    description: `<div>
          <div>Combine a date and a time together to create a new combined date time.</div>
          <br />
          <div>
            Eg: concatenate-datetime(the arrival date, the arrival time) would return a date time combining the two.
          </div>
          <br />
          <div>
            Note that the platform treats all dates and times as combined date times, so in many cases you won't need
            this function. Just make sure your API or interview requests date times
          </div>
        </div>`,
  }),
  new Command({
    id: "is-member-of",
    arguments: [ Argument.required("entity", "text"), Argument.required("relationship", "relationship") ],
    definition: [ /^(.+) is a member of (.+)$/ ],
    displayName: "Is Member Of",
    description: `<div>
          <div>
            Infer membership of instances of an entity to a relationship. Must be the conclusion of a rule.
          </div>
          <br />
          <div>
            Eg: is-member-of(the child, all male children)
            <br />
            Assuming the child is male, would add any instance of the entity 'the child' that satisfied the condition of the rule as a target of the relationship 'the male children'.
          </div>
          <br />
        </div>`,
  }),
  INFER_INSTANCE_COMMAND,
  INFER_INSTANCES_COMMAND,
  new Command({
    id: "infer-instances-from-temporal",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("start", "datetime"),
      Argument.required("end", "datetime"),
    ],
    displayName: "Infer Instance From Temporal",
  }),
  new Command({
    id: "infer-instances-from-list",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("list", "set"),
      Argument.required("listElement", "auto"),
    ],
    displayName: "Infer Instance From List",
  }),
  new Command({
    id: "make-date",
    arguments: [
      Argument.required("year", "number"),
      Argument.required("month", "number"),
      Argument.required("day", "number"),
    ],
    displayName: "Make Date",
    definition: [],
  }),
  new Command({
    id: "make-time",
    arguments: [
      Argument.required("hour", "number"),
      Argument.required("minute", "number"),
      Argument.required("second", "number"),
    ],
    displayName: "Make Time",
    definition: [],
  }),
  new Command({
    id: "make-date-time",
    alias: [ "make-datetime" ],
    arguments: [
      Argument.required("year", "number"),
      Argument.required("month", "number"),
      Argument.required("day", "number"),
      Argument.required("hour", "number"),
      Argument.required("minute", "number"),
      Argument.required("second", "number"),
    ],
    displayName: "Make Date Time",
    definition: [],
  }),
  new Command({
    id: "instance-equals",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [],
    displayName: "Instance Equals",
  }),
  new Command({
    id: "instance-not-equals",
    arguments: [ Argument.required("a", "auto"), Argument.required("b", "auto") ],
    definition: [],
    displayName: "Instance Not Equals",
  }),
  new Command({
    id: "earliest",
    arguments: [],
    definition: [],
    displayName: "Earliest",
  }),
  new Command({
    id: "latest",
    arguments: [],
    definition: [],
    displayName: "Latest",
  }),
  new Command({
    id: "get-data",
    arguments: [
      Argument.required("documentName", "string"),
      Argument.required("key", "string"),
      Argument.required("value", "string"),
    ],
    displayName: "Get Data",
  }),
  new Command({
    id: "get-spreadsheet-cell",
    arguments: [
      Argument.required("file", "file"),
      Argument.required("cell", "string"),
      Argument.optional("sheet", "string"),
    ],
    displayName: "Get Spreadsheet Cell",
    description: `<div>
          <div>
            Gets the value of a cell from a file attribute which contains a spreadsheet.
          </div>
          <br />
          <div>Eg: get-spreadsheet-cell(the file, "A1", "sheet 2")</div>
        </div>`,
  }),
  new Command({
    id: "day-difference-inclusive",
    arguments: [ Argument.required("a", "datetime"), Argument.required("b", "datetime") ],
    //definition: [/^the difference in days between (.+) and (.+) inclusive$/],
    displayName: "Day Difference Inclusive",
    description: `<div>
          <div>
            Return the difference in days (rounded down to the nearest whole number) between two dates. Note: this
            function will return a negative number if the first date is greater than the second date.
          </div>
          <br />
          <div>Eg: day-difference(2020-01-01, 2020-01-06) would return 6.</div>
          <br />
          <div>Consider day-difference if you don't want to include the end day.</div>
        </div>`,
  }),
  new Command({
    id: "when-next-skip",
    arguments: [
      Argument.required("date", "datetime"),
      Argument.required("count", "number"),
      Argument.required("condition", "boolean"),
    ],
    displayName: "When Next Skip",
  }),
  new Command({
    id: "when",
    arguments: [ Argument.required("condition", "boolean") ],
    displayName: "When",
    hidden: true,
  }),
  new Command({
    id: "previous",
    arguments: [ Argument.required("attribute", "auto") ],
    displayName: "Previous",
    description: `<div>
      Return the previous value of the provided attribute before the current value.
    </div>`,
  }),
  /*new Command({
    id: "temporal-is-holiday",
    arguments: [
      Argument.required("the region", "string"),
      Argument.required("the start date", "datetime"),
      Argument.required("the end date", "string"),
    ],
    definition: [],
    displayName: "Temporal Is Holiday",
  }),*/

  VALIDATE_COMMAND,
  WARNING_COMMAND,
  CONTACT_START_COMMAND,
  CONTACT_END_COMMAND,

  ORDINAL_COMMAND,

  new Command({
    id: "instance-value-from-ordinal",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("value", "auto"),
      Argument.required("ordinal", "number"),
    ],
    displayName: "Instance Value From Ordinal",
  }),

  new Command({
    id: "set-from-instance-values-if",
    arguments: [
      Argument.required("relationship", "relationship"),
      Argument.required("attribute", "auto"),
      Argument.required("condition", "boolean"),
    ],
    displayName: "Instance Value From Ordinal",
  }),
];
